import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowUpRight, LogOut, LayoutGrid } from 'lucide-react';
import { navigation } from '../mock';
import { Button } from './ui/button';
import { useLeadDialog } from '../context/LeadDialogContext';
import { useAuth } from '../context/AuthContext';
import { useSignInDialog } from '../context/SignInDialogContext';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';

// To re-enable the Customers menu, add 'customers' back into this array.
const tabs = ['platform', 'solutions', 'services', 'resources', 'company'];

const Navbar = () => {
  const [active, setActive] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const { open: openLead } = useLeadDialog();
  const { user, logout } = useAuth();
  const { open: openSignIn } = useSignInDialog();
  const myPortal = user?.role === 'employee' ? '/employee-portal' : user?.role === 'partner' ? '/partner-portal' : null;
  const initials = (user?.name || user?.email || '?').trim().split(/\s+/).map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  const headerRef = useRef(null);
  // Close the mega-menu on outside click or Escape
  useEffect(() => {
    if (!active) return;
    const onDocClick = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setActive(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setActive(null); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [active]);

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-50 bg-black/85 backdrop-blur border-b border-neutral-900"
      onMouseLeave={() => active && setActive(null)}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 sm:gap-2.5 group min-w-0">
            <img
              src="https://customer-assets.emergentagent.com/job_metaxuone-preview/artifacts/kjo0ib4e_Logo.png"
              alt="MetaxuOne"
              className="h-8 w-8 sm:h-9 sm:w-9 object-contain flex-shrink-0"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_metaxuone-preview/artifacts/ir3od9nj_company%20name.jpg"
              alt="METAXUONE"
              className="h-4 sm:h-5 object-contain"
              style={{ mixBlendMode: 'screen' }}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setActive(active === t ? null : t)}
                onDoubleClick={() => setActive(t)}
                className={`px-3 py-2 text-[14px] font-medium rounded-md inline-flex items-center gap-1 transition-colors hover:text-blue-400 ${active === t ? 'text-blue-400' : 'text-neutral-300'}`}
              >
                {navigation[t].title}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${active === t ? 'rotate-180' : ''}`} />
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="inline-flex items-center gap-2 rounded-full pl-1.5 pr-3 h-9 border border-neutral-800 hover:border-neutral-600 transition-colors">
                    {user.picture ? (
                      <img src={user.picture} alt="User" className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/40 text-blue-300 text-[11px] font-semibold flex items-center justify-center">
                        {initials}
                      </span>
                    )}
                    <span className="text-[13px] text-white max-w-[140px] truncate">{user.name || user.email}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-neutral-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-neutral-950 border border-neutral-800 text-white min-w-[220px]" align="end">
                  <DropdownMenuLabel className="text-neutral-400 text-[11px] uppercase tracking-wider">
                    Signed in · <span className="text-neutral-300 normal-case tracking-normal">{user.role}</span>
                  </DropdownMenuLabel>
                  <div className="px-2 pb-2 text-[12px] text-neutral-400 truncate">{user.email}</div>
                  <DropdownMenuSeparator className="bg-neutral-800" />
                  {myPortal && (
                    <DropdownMenuItem asChild className="focus:bg-neutral-900 focus:text-white cursor-pointer">
                      <Link to={myPortal} className="flex items-center gap-2">
                        <LayoutGrid className="w-4 h-4" />
                        {user.role === 'employee' ? 'Employee Portal' : 'Partner Portal'}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={logout}
                    className="focus:bg-neutral-900 focus:text-white cursor-pointer text-red-300 focus:text-red-200"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="ghost"
                onClick={() => openSignIn('signin')}
                className="text-[14px] font-medium text-neutral-200 hover:bg-neutral-900 hover:text-white"
              >
                Sign in
              </Button>
            )}
            <Button
              onClick={() => openLead({ source: 'navbar-get-started', title: 'Get started with MetaxuOne' })}
              className="bg-white hover:bg-neutral-200 text-neutral-950 rounded-full px-4 h-9 text-[14px] font-medium"
            >
              Get started
            </Button>
          </div>

          <button className="lg:hidden p-2 text-white" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mega menu */}
      {active && (
        <div className="hidden lg:block absolute left-0 right-0 top-16 bg-black border-b border-neutral-900 shadow-[0_24px_40px_-20px_rgba(0,0,0,0.6)]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-3">
                <p className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Menu</p>
                <h3 className="text-2xl font-semibold text-white">{navigation[active].title}</h3>
                <p className="text-sm text-neutral-400 mt-3 max-w-xs">
                  Explore everything MetaxuOne offers under {navigation[active].title.toLowerCase()}.
                </p>
              </div>
              <div className={`col-span-9 grid gap-8 ${
                navigation[active].sections.length >= 3 ? 'grid-cols-3'
                : navigation[active].sections.length === 2 ? 'grid-cols-2'
                : 'grid-cols-1'
              }`}>
                {navigation[active].sections.map((section) => (
                  <div key={section.heading}>
                    <p className="text-xs uppercase tracking-wider text-neutral-500 mb-4">{section.heading}</p>
                    <ul className="space-y-1">
                      {section.items.map((item) => (
                        <li key={item.label}>
                          <Link
                            to={item.href}
                            onClick={() => setActive(null)}
                            className="group flex items-start gap-3 p-3 -mx-3 rounded-lg hover:bg-neutral-900 transition-colors"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[15px] font-semibold text-white group-hover:text-neutral-200">{item.label}</span>
                                <ArrowUpRight className="w-3.5 h-3.5 text-neutral-500 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                              </div>
                              <p className="text-[13px] text-neutral-400 mt-0.5 leading-snug">{item.description}</p>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-neutral-900 bg-black max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            {tabs.map((t) => {
              const isExpanded = mobileExpanded === t;
              return (
                <div key={t} className="border-b border-neutral-900">
                  <button
                    onClick={() => setMobileExpanded(isExpanded ? null : t)}
                    className="w-full flex items-center justify-between py-3 text-[15px] font-medium text-neutral-100"
                    aria-expanded={isExpanded}
                  >
                    <span className={isExpanded ? 'text-blue-400' : ''}>{navigation[t].title}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180 text-blue-400' : 'text-neutral-500'}`} />
                  </button>
                  {isExpanded && (
                    <div className="pb-3 pl-3 space-y-4">
                      {navigation[t].sections.map((section) => (
                        <div key={section.heading}>
                          <p className="text-[10.5px] uppercase tracking-wider text-neutral-500 mb-2">{section.heading}</p>
                          <ul className="space-y-1">
                            {section.items.map((item) => (
                              <li key={item.label}>
                                <Link
                                  to={item.href}
                                  onClick={() => { setMobileOpen(false); setMobileExpanded(null); setActive(null); }}
                                  className="block py-2 -mx-2 px-2 rounded-md hover:bg-neutral-900"
                                >
                                  <div className="text-[14px] font-medium text-white">{item.label}</div>
                                  <div className="text-[12px] text-neutral-400 leading-snug">{item.description}</div>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="flex gap-2 pt-4">
              {user ? (
                <Button
                  variant="outline"
                  onClick={() => { setMobileOpen(false); logout(); }}
                  className="flex-1 border-neutral-700 bg-transparent text-white hover:bg-neutral-900"
                >
                  Sign out
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => { setMobileOpen(false); openSignIn('signin'); }}
                  className="flex-1 border-neutral-700 bg-transparent text-white hover:bg-neutral-900"
                >
                  Sign in
                </Button>
              )}
              <Button
                onClick={() => { setMobileOpen(false); openLead({ source: 'mobile-get-started' }); }}
                className="flex-1 bg-white text-neutral-950"
              >
                Get started
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
